import { useCallback, useEffect, useState } from 'react';
import { api, mensajeDe, type EstadoRespaldo } from '../../services/api';
import { Aviso, Boton, Tarjeta, Titulo } from '../../components/ui';

/**
 * El respaldo semanal de la base. Lo hace el sitio solo; acá se ve cuándo fue
 * el último, y avisa si se atrasó: un respaldo que falla en silencio es peor
 * que no tener ninguno, porque uno cree que lo tiene.
 */
export function Respaldo({ alCambiar }: { alCambiar?: (estado: EstadoRespaldo) => void } = {}) {
  const [estado, setEstado] = useState<EstadoRespaldo | null>(null);
  const [error, setError] = useState('');
  const [haciendo, setHaciendo] = useState(false);

  const traer = useCallback(async () => {
    try {
      const nuevo = await api.respaldo();
      setEstado(nuevo);
      alCambiar?.(nuevo);
    } catch (e) {
      setError(mensajeDe(e));
    }
  }, [alCambiar]);

  useEffect(() => {
    void traer();
  }, [traer]);

  const hacer = async () => {
    setHaciendo(true);
    setError('');
    try {
      await api.respaldarAhora();
      await traer();
    } catch (e) {
      setError(mensajeDe(e));
    } finally {
      setHaciendo(false);
    }
  };

  if (!estado) return error ? <Aviso tono="error">{error}</Aviso> : null;

  const cuando =
    estado.dias === null ? 'Todavía no se hizo ninguno.' : estado.dias === 0 ? 'El último es de hoy.' : `El último es de hace ${estado.dias} día${estado.dias === 1 ? '' : 's'}.`;

  return (
    <Tarjeta className={estado.atrasado ? 'border-(--color-alerta)' : ''}>
      <Titulo>Respaldo</Titulo>
      {!estado.configurado ? (
        <Aviso tono="alerta">Este sitio no tiene dónde guardar respaldos. Avisale a quien lo mantiene.</Aviso>
      ) : (
        <>
          <p className="text-sm text-(--color-apagado)">
            El sitio guarda solo una copia de todos los datos una vez por semana. {cuando}
            {estado.bytes ? ` Pesa ${Math.max(1, Math.round(estado.bytes / 1024))} KB.` : ''}
          </p>
          {estado.atrasado && estado.dias !== null && (
            <div className="mt-2">
              <Aviso tono="alerta">Tendría que haberse hecho uno esta semana y no se hizo. Hacé uno ahora y avisale a quien mantiene el sitio.</Aviso>
            </div>
          )}
          {error && (
            <div className="mt-2">
              <Aviso tono="error">{error}</Aviso>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Boton chico tipo="secundario" onClick={hacer} deshabilitado={haciendo}>
              {haciendo ? 'Guardando...' : 'Hacer uno ahora'}
            </Boton>
            {estado.ultimo !== null && (
              <a
                href="/api/admin/respaldo/descargar"
                className="flex min-h-9 items-center rounded-xl border border-(--color-borde) bg-(--color-panel) px-3 text-sm"
              >
                Bajar el último
              </a>
            )}
          </div>
        </>
      )}
    </Tarjeta>
  );
}
